import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import styles from "./RecipeList.module.css";
import { open_api_with_cookies } from "../../Utils/network";
import RecipeBanner from "../../Components/Card/RecipeBanner";
import { convertToReadableTime } from "../../Utils/helper";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Spinning loader for infinite scroll
const LoadingSpinner = () => (
  <div className={styles.spinner_container}>
    <div className={styles.spinner}></div>
  </div>
);

// Skeleton for recipe cards
const RecipeCardSkeleton = () => (
  <div className={styles.recipe_skeleton}>
    <Skeleton height={200} width="100%" style={{ marginBottom: "12px", borderRadius: 16 }} />
    <Skeleton height={22} width="80%" style={{ marginBottom: "10px" }} />
    <Skeleton height={16} width="55%" style={{ marginBottom: "10px" }} />
    <Skeleton height={14} width="40%" />
  </div>
);

// The backend paginates /recipe/filterlist/ (DRF, 100 per page). Follow the
// `next` links so every recipe is loaded, then show newest first.
const fetchAllRecipes = async (query = "") => {
  let all = [];
  let search = query;
  for (let guard = 0; guard < 20 && search !== null; guard++) {
    const result = await open_api_with_cookies.get(
      `/recipe/filterlist/${search}`
    );
    all = all.concat(result.data.results || []);
    // `next` is an absolute URL; keep only its query string so the request
    // goes through our configured API client (base URL differs per env).
    search = result.data.next ? new URL(result.data.next).search : null;
  }
  // Drop recipes without a slug — they have no detail page to link to.
  return all.filter((r) => r && r.slug).sort((a, b) => b.id - a.id);
};

export default function RecipeList() {
  const navigate = useNavigate();
  const [recipeList, setRecipeList] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [selectedChips, setSelectedChips] = useState(["all"]);
  const [dietFilter, setDietFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [chips, setChips] = useState([]);
  const ITEMS_PER_PAGE = 12;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const sentinelRef = useRef(null);

  const visibleRecipes = filteredRecipes.slice(0, visibleCount);
  const hasMore = visibleCount < filteredRecipes.length;

  const getList = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const results = await fetchAllRecipes();
      setRecipeList(results);
      setFilteredRecipes(results);
      setVisibleCount(ITEMS_PER_PAGE);
    } catch (err) {
      setError("Failed to load recipes. Please try again later.");
      console.error("Error fetching recipes:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Browser scroll anchoring fights infinite scroll: with the page bottom in
  // view it anchors to the footer, so every appended batch auto-scrolls the
  // page down, keeping the sentinel visible and chain-loading the whole list.
  useEffect(() => {
    const html = document.documentElement.style;
    const body = document.body.style;
    const prev = [html.overflowAnchor, body.overflowAnchor];
    html.overflowAnchor = "none";
    body.overflowAnchor = "none";
    return () => {
      html.overflowAnchor = prev[0];
      body.overflowAnchor = prev[1];
    };
  }, []);

  // Reveal the next batch only when the sentinel below the grid scrolls into
  // view. The functional update makes repeated observer callbacks harmless.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || isLoading || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((count) => count + ITEMS_PER_PAGE);
        }
      },
      { rootMargin: "200px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [isLoading, hasMore, visibleCount]);

  const getChips = async () => {
    try {
      const result = await open_api_with_cookies.get(`recipe/filter-chips/`);
      const allChip = { id: 0, name: "all", display_name: "All", visible: true };
      setChips([allChip, ...result.data.results]);
    } catch (err) {
      console.error("Error fetching chips:", err);
    }
  };

  const filter = async () => {
    try {
      if (selectedChips.includes("all") && !searchQuery) {
        setFilteredRecipes(recipeList);
        setVisibleCount(ITEMS_PER_PAGE);
        return;
      }

      setIsLoading(true);
      setError(null);
      const results = await fetchAllRecipes(
        `?chips=${encodeURIComponent(selectedChips)}&search=${encodeURIComponent(searchQuery)}`
      );
      setFilteredRecipes(results);
      setVisibleCount(ITEMS_PER_PAGE);
      setIsLoading(false);
    } catch (err) {
      setError("Failed to filter recipes. Please try again.");
      console.error("Error filtering recipes:", err);
      setIsLoading(false);
    }
  };

  const handleChipClick = (chipName) => {
    setSelectedChips((prev) => {
      if (chipName === "all") return ["all"];
      if (prev.includes(chipName)) {
        const next = prev.filter((c) => c !== "all" && c !== chipName);
        return next.length ? next : ["all"];
      }
      return [...prev.filter((c) => c !== "all"), chipName];
    });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(searchTerm.trim());
  };

  const handleRandomRecipe = () => {
    if (recipeList.length === 0) return;
    setIsLoading(true);
    setTimeout(() => {
      const randomRecipe =
        recipeList[Math.floor(Math.random() * recipeList.length)];
      if (randomRecipe && randomRecipe.slug) {
        navigate(`/recipe/${randomRecipe.slug}`);
      } else {
        setError("Couldn't find a random recipe. Please try again.");
        setIsLoading(false);
      }
    }, 300);
  };

  const goToRecipe = (slug) => slug && navigate(`/recipe/${slug}`);

  useEffect(() => {
    getList();
    getChips();
  }, []);

  useEffect(() => {
    filter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChips, searchQuery]);

  // The API's diet_type values are "veg" | "nonveg" | "contains_egg"
  const dietMatch = (r) => {
    if (dietFilter === "all") return true;
    if (dietFilter === "veg") return r.diet_type !== "nonveg";
    return r.diet_type === "nonveg";
  };

  const isDefaultView = selectedChips.includes("all") && !searchQuery;
  const featured = isDefaultView ? filteredRecipes[0] : null;
  const gridSource = featured ? visibleRecipes.slice(1) : visibleRecipes;
  const displayRecipes = gridSource.filter(dietMatch);
  const collageTiles = (recipeList.length ? recipeList : filteredRecipes)
    .slice(0, 3)
    .map((r) => r.banner)
    .filter(Boolean);

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Healthy Recipes for Kids &amp; Families | Myburgo</title>
        <meta
          name="description"
          content="Dietician-approved, baby-led and toddler-loved recipes. Wholesome Indian meals with step-by-step guidance — from first purées to lunchbox heroes."
        />
        <link rel="canonical" href="https://myburgo.com/recipe" />
      </Helmet>

      {/* ambient blobs */}
      <div className={styles.ambient} aria-hidden="true">
        <span className={`${styles.blob} ${styles.blob1}`} />
        <span className={`${styles.blob} ${styles.blob2}`} />
        <span className={`${styles.blob} ${styles.blob3}`} />
      </div>

      <main className={styles.wrap}>
        {/* ───────── HERO ───────── */}
        <section className={styles.hero}>
          <div className={styles.heroCopy}>
            <span className={styles.eyebrow}>Nourishing little ones, simply</span>
            <h1 className={styles.heroTitle}>
              Recipes worth <em>cooking</em>
              <br />
              for your family.
            </h1>
            <p className={styles.heroSub}>
              Dietician-approved, baby-led and toddler-loved. Wholesome Indian
              meals with step-by-step guidance — from first purées to lunchbox
              heroes.
            </p>
            <form className={styles.search} onSubmit={handleSearchSubmit}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                placeholder="Search dosa, millet, purée, finger foods…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search recipes"
              />
              <button type="submit" className={styles.searchBtn}>
                Search
              </button>
            </form>
            <button
              type="button"
              className={styles.surprise}
              onClick={handleRandomRecipe}
            >
              🎲 Surprise me with a recipe
            </button>
          </div>

          <div className={styles.collage} aria-hidden="true">
            <div
              className={`${styles.tile} ${styles.tile1}`}
              style={collageTiles[0] ? { backgroundImage: `url(${collageTiles[0]})` } : undefined}
            />
            <div
              className={`${styles.tile} ${styles.tile2}`}
              style={collageTiles[1] ? { backgroundImage: `url(${collageTiles[1]})` } : undefined}
            />
            <div
              className={`${styles.tile} ${styles.tile3}`}
              style={collageTiles[2] ? { backgroundImage: `url(${collageTiles[2]})` } : undefined}
            />
            <span className={`${styles.sticker} ${styles.sticker1}`}>
              🥗 {recipeList.length || "200"}+ recipes
            </span>
            <span className={`${styles.sticker} ${styles.sticker2}`}>
              <span className={`${styles.dietDot} ${styles.veg}`} /> Veg-first
            </span>
          </div>
        </section>

        {/* ───────── FILTER BAR ───────── */}
        <div className={styles.filterbar}>
          <div className={styles.filterbarInner}>
            <div className={styles.chips} role="group" aria-label="Filter recipes by category">
              {chips.map((chip) => {
                const active = selectedChips.includes(chip.name);
                return (
                  <button
                    key={chip.id}
                    type="button"
                    className={`${styles.chip} ${active ? styles.chipActive : ""}`}
                    onClick={() => handleChipClick(chip.name)}
                    aria-pressed={active}
                  >
                    {chip.display_name}
                  </button>
                );
              })}
            </div>
            <div className={styles.dietToggle}>
              {["all", "veg", "non_veg"].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`${styles.dietBtn} ${dietFilter === d ? styles.dietOn : ""} ${
                    d === "veg" ? styles.tVeg : d === "non_veg" ? styles.tNon : ""
                  }`}
                  onClick={() => setDietFilter(d)}
                >
                  {d === "all" ? "All" : d === "veg" ? "Veg" : "Non-veg"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <p className={styles.error_message} role="alert">
            {error}
          </p>
        )}

        {/* ───────── FEATURED ───────── */}
        {!isLoading && featured && dietMatch(featured) && (
          <section className={styles.sec}>
            <div className={styles.secHead}>
              <div>
                <span className={styles.eyebrow}>Recipe of the week</span>
                <h2 className={styles.secTitle}>Handpicked for you</h2>
              </div>
            </div>
            <article
              className={styles.featured}
              onClick={() => goToRecipe(featured.slug)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && goToRecipe(featured.slug)
              }
            >
              <div
                className={styles.featuredMedia}
                style={featured.banner ? { backgroundImage: `url(${featured.banner})` } : undefined}
              >
                <span className={styles.featuredBadge}>⭐ Editor's pick</span>
              </div>
              <div className={styles.featuredBody}>
                {(featured.category || featured.cuisine) && (
                  <span className={styles.featuredCat}>
                    {featured.category || featured.cuisine}
                  </span>
                )}
                <h3 className={styles.featuredTitle}>{featured.title}</h3>
                <p className={styles.featuredDesc}>
                  {featured.description
                    ? featured.description.substring(0, 160)
                    : `A wholesome, family-friendly recipe${
                        featured.chef_details?.first_name
                          ? ` from ${featured.chef_details.first_name}`
                          : ""
                      }. Tap through for the ingredients and step-by-step directions.`}
                </p>
                <div className={styles.featuredMeta}>
                  {featured.preparation_time && (
                    <span className={styles.pill}>
                      ⏱ {convertToReadableTime(featured.preparation_time)}
                    </span>
                  )}
                  <span className={styles.pill}>
                    <span className={`${styles.dietDot} ${featured.diet_type === "nonveg" ? styles.nonveg : styles.veg}`} />
                    {featured.diet_type === "nonveg" ? "Non-veg" : "Veg"}
                  </span>
                  {featured.view_count > 0 && (
                    <span className={styles.pill}>👁 {featured.view_count} views</span>
                  )}
                </div>
                <div className={styles.featuredChef}>
                  {featured.chef_details?.image ? (
                    <img src={featured.chef_details.image} alt="" />
                  ) : (
                    <span className={styles.chefFallback} />
                  )}
                  <div>
                    <b>
                      {featured.chef_details?.prefix
                        ? `${featured.chef_details.prefix} `
                        : ""}
                      {featured.chef_details?.first_name}{" "}
                      {featured.chef_details?.last_name}
                    </b>
                    <span>View full recipe →</span>
                  </div>
                </div>
              </div>
            </article>
          </section>
        )}

        {/* ───────── GRID ───────── */}
        <section className={styles.sec}>
          <div className={styles.secHead}>
            <h2 className={styles.secTitle}>
              {isDefaultView ? "Fresh from the kitchen" : "Results"}
            </h2>
            {!isLoading && filteredRecipes.length > 0 && (
              <span className={styles.count}>
                {filteredRecipes.length} recipe
                {filteredRecipes.length === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {isLoading ? (
            <div className={styles.grid} aria-live="polite">
              {Array(8)
                .fill(0)
                .map((_, index) => (
                  <RecipeCardSkeleton key={index} />
                ))}
            </div>
          ) : displayRecipes.length > 0 ? (
            <>
              <div className={styles.grid}>
                {displayRecipes.map((element, index) => (
                  <div
                    className={styles.cardWrap}
                    key={element.slug || index}
                    onClick={() => goToRecipe(element.slug)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goToRecipe(element.slug);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${element.title} recipe`}
                  >
                    <RecipeBanner
                      chefDetails={element.chef_details}
                      recipe={element}
                    />
                  </div>
                ))}
              </div>
              {hasMore && (
                <div ref={sentinelRef} className={styles.sentinel}>
                  <LoadingSpinner />
                </div>
              )}
            </>
          ) : (
            <div className={styles.no_results} role="status">
              <p>No recipes found matching your criteria.</p>
              <button
                className={styles.reset_button}
                onClick={() => {
                  setSelectedChips(["all"]);
                  setSearchQuery("");
                  setSearchTerm("");
                  setDietFilter("all");
                }}
              >
                Reset filters
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
